import { Lap } from '@model/lap';

type LapImagePath = `assets/lap/${number}.${number}.png`;

/**
 * Returns the most recent non-null/non-undefined time value from a lap.
 *
 * The participant times are checked in this order:
 * - startTime
 * - transitTime1
 * - transitTime2
 * - transitTime3
 * - transitTime4
 * - finishTime
 *
 * If `lap` is undefined or all time fields are null/undefined, returns `undefined`.
 *
 * @param lap The lap whose latest available time should be determined.
 * @returns The latest available time as a string, or undefined if none is available.
 */
export const getLastUpdatedTime = (lap?: Lap): string | undefined => {
  if (!lap) {
    return undefined;
  }

  const [lastUpdatedTime] = [
    lap.startTime,
    lap.transitTime1,
    lap.transitTime2,
    lap.transitTime3,
    lap.transitTime4,
    lap.finishTime,
  ]
    .filter(time => time !== null && time !== undefined)
    .reverse();

  return lastUpdatedTime;
};

const getLapImagePathForTimeRegistration = (
  lapNumber: number,
  timeRegistrationNumber: number,
): LapImagePath => `assets/lap/${lapNumber}.${timeRegistrationNumber}.png`;

/**
 * Returns the image path that represents the participant's current lap state.
 *
 * For laps with a `lapNumber` greater than 6, the last image (6.6) is used.
 * Otherwise, the image is chosen based on the lap number and the latest
 * registered time (start, transit, or finish).
 *
 * @param lap The lap for which to determine the representative image.
 * @returns The image path as a string, or undefined when no lap is provided.
 */
export const getImagePathForLap = (lap?: Lap): LapImagePath | undefined => {
  if (!lap) {
    return undefined;
  }

  if (lap.lapNumber > 6) {
    return getLapImagePathForTimeRegistration(6, 6);
  } else {
    return getLapImagePathForTimeRegistration(
      lap.lapNumber,
      getTimeRegistrationNumber(lap),
    );
  }
};

/**
 * Determines which time registration step (1–6) the participant is currently at.
 *
 * The number corresponds to the latest available time field on the lap:
 * 1 = startTime, 2 = transitTime1, 3 = transitTime2,
 * 4 = transitTime3, 5 = transitTime4, 6 = finishTime.
 * If no times are set, 0 is returned.
 *
 * @param lap The lap whose time registration progress should be evaluated.
 * @returns A number between 0 and 6 indicating the current time registration.
 */
export const getTimeRegistrationNumber = (lap: Lap): number => {
  if (lap.finishTime) {
    return 6;
  } else if (lap.transitTime4) {
    return 5;
  } else if (lap.transitTime3) {
    return 4;
  } else if (lap.transitTime2) {
    return 3;
  } else if (lap.transitTime1) {
    return 2;
  } else if (lap.startTime) {
    return 1;
  } else {
    return 0;
  }
};
